'use client';

import { FaChevronLeft, FaChevronRight } from 'react-icons/fa6';

import { MdOutlineToday } from 'react-icons/md';
import { FiHelpCircle, FiInfo, FiPlus } from 'react-icons/fi';
import type { Placement } from '@floating-ui/react';
import { Popover } from '@/app/components/ui/Popover';
import { cn } from '@/utils/uiUtils';

interface QAStepsProps {
  className?: string;
  placement?: Placement;
}

export function TipsWorkoutSteps({
  className,
  placement = 'bottom-start',
}: QAStepsProps) {
  return (
    <Popover
      placement={placement}
      aria-labelledby='default-popover'
      content={
        <div className='popover'>
          <h4>Example Steps</h4>
          <p>
            <div>5 min Z2 HR Warm up</div>
            <div>20 min Z3 HR active</div>
            <div>5 min Z1 HR cool down</div>
          </p>
          <p>
            You can manually change the steps to your own. Each row represents a
            step in your workout. '5m' indicates a duration of 5 minutes, 'Z2'
            refers to heart rate zone two, and 'HR' stands for heart rate. These
            are used to plot the chart. The additional text provides short
            guidance for you to follow while using your Garmin devices during
            the activity.
          </p>
          <p>
            Feelings are how do you feel on your body. and RPE is your
            subjective relative projected effort of the workout.
          </p>
        </div>
      }
    >
      <button className={cn('btn btn-ghost p-1', className)}>
        <FiInfo className='w-4 h-4 md:w-6 md:h-6' />
      </button>
    </Popover>
  );
}

interface TipsSettingsSelectSportsProps {
  placement?: Placement;
}
export function TipsSettingsSelectSports({
  placement = 'bottom-start',
}: TipsSettingsSelectSportsProps) {
  return (
    <Popover
      placement={placement}
      content={
        <div>
          Select the sports you have access to the facilities for. Your coach
          will use some of these for cross training, and your calendar will only
          display activities and workouts for these selected sports.
        </div>
      }
    >
      <button className='btn btn-ghost p-1'>
        <FiHelpCircle className='w-4 h-4 md:w-6 md:h-6' />
      </button>
    </Popover>
  );
}

interface TipsSettingsChooseCoachProps {
  placement?: Placement;
}
export function TipsSettingsChooseCoach({
  placement = 'bottom-start',
}: TipsSettingsChooseCoachProps) {
  return (
    <Popover
      placement={placement}
      content={
        <div className='popover max-w-md'>
          Each AI coach is an agent that focuses on certain sports and goals.
          Please feel free to try out a few coaches to find the best fit for
          you.
        </div>
      }
    >
      <button className='btn btn-ghost p-1'>
        <FiHelpCircle className='w-4 h-4 md:w-6 md:h-6' />
      </button>
    </Popover>
  );
}

interface TipsSettingsHRZonesProps {
  placement?: Placement;
}
export function TipsSettingsHRZones({
  placement = 'bottom-start',
}: TipsSettingsHRZonesProps) {
  return (
    <Popover
      placement={placement}
      content={
        <div>
          <p>
            Your coach will use your maximum heart rate and resting heart rate
            to calculate your training heart rate zones.
          </p>
          <p>
            If you are using Garmin Connect, and have changed your HR zones
            there, you can manually match it here. By default, we use the same
            as Garmin default.
          </p>
        </div>
      }
    >
      <button className='btn btn-ghost p-1'>
        <FiHelpCircle className='w-4 h-4 md:w-6 md:h-6' />
      </button>
    </Popover>
  );
}

interface TipsCalendarProps {
  placement?: Placement;
}
export function TipsCalendar({
  placement = 'bottom-start',
}: TipsCalendarProps) {
  return (
    <Popover
      placement={placement}
      content={
        <div className='popover'>
          <h2 className='h3'>Training Calendar</h2>
          <p>
            The calendar for your training schedule allows you to track your
            progress. Your activities (DONE) and workouts (PLAN) are shown in
            the calendar as a list of cards. Each week shows a summary in the
            header, and each card displays the activity or workout type,
            duration, and heart rate zone.
          </p>
          <p>
            You can click on an activity card to see the details.
            <div className='cursor-pointer border-l-4 rounded-lg border-green-400 my-4 p-8 text-[1.2em]'>
              An Activity
            </div>
          </p>
          <p>
            You can click on a workout card to edit the workout.
            <div className='cursor-pointer border-l-4 rounded-lg border-yellow-400 my-4 p-8 text-[1.2em]'>
              A Workout
            </div>
          </p>
          <p>
            Usually, you can ask your coach to create a workout. You can click
            <button className='btn btn-info inline-block mx-4 p-2'>
              <FiPlus size={8} />
            </button>
            to manually add a new workout for that day.
          </p>
          <p>
            Navigate to last month by clicking
            <button className='inline-block mx-4 p-2 btn btn-secondary rounded-full'>
              <FaChevronLeft size={16} />
            </button>
            or next month by clicking
            <button className='inline-block mx-4 p-2 btn btn-secondary rounded-full'>
              <FaChevronRight size={16} />
            </button>
            . Quickly navigate to today by clicking
            <MdOutlineToday size={24} className='inline-block link mx-4' />.
          </p>
        </div>
      }
    >
      <button className='btn btn-ghost p-1'>
        <FiHelpCircle size={24} />
      </button>
    </Popover>
  );
}
